// ============================================================================
// ACTION MODAL PRODUCTION SERVICE
// ============================================================================
// Reads from song_modal_blocks and song_pathway_cards tables
// Replaces JSONB-based action_modals_by_level field

import { supabase } from './supabaseClient';

export interface ModalBlock {
  id: string;
  song_id: string;
  civic_level_id: string;
  modal_key: string;
  block_type: 'richText' | 'prompt' | 'checklist' | 'callout' | 'insight_chips';
  sort_order: number;
  enabled: boolean;
  status: 'draft' | 'published';
  tone: 'neutral' | 'reflective' | 'strategic' | null;
  block_title: string | null;
  block_body: string | null;
  block_prompt: string | null;
  items: string[] | null;
  content?: any;
  meta: any;
  created_at: string;
  updated_at: string;
}

export interface PathwayCard {
  id: string;
  song_id: string;
  civic_level_id: string;
  status: 'draft' | 'published';
  enabled: boolean;
  sort_order: number;
  title: string;
  description: string;
  action_type: string;
  region_scope: string | null;
  cta_label: string;
  url: string | null;
  source_tag: string | null;
  created_at: string;
  updated_at: string;
}

export interface CivicLevel {
  id: string;
  name: string;
  slug: string;
  level_number: number;
}

// ============================================================================
// FETCH CIVIC LEVELS
// ============================================================================

let cachedCivicLevels: CivicLevel[] | null = null;

export async function getCivicLevels(): Promise<CivicLevel[]> {
  if (cachedCivicLevels) {
    return cachedCivicLevels;
  }

  const { data, error } = await supabase
    .from('civic_levels')
    .select('*')
    .order('level_number');

  if (error) {
    console.error('[getCivicLevels] Error:', error);
    return [];
  }

  cachedCivicLevels = data || [];
  return cachedCivicLevels;
}

export async function getCivicLevelBySlug(slug: string): Promise<CivicLevel | null> {
  const levels = await getCivicLevels();
  return levels.find(l => l.slug === slug) || null;
}

// ============================================================================
// FETCH MODAL BLOCKS
// ============================================================================

export interface FetchBlocksOptions {
  songId: string;
  civicLevelSlug: string;
  modalKey: 'look_deeper' | 'one_key_insight' | 'explore_connections' | 'take_it_further';
  includeDrafts?: boolean; // Admin or Preview mode
}

export async function fetchModalBlocks(
  options: FetchBlocksOptions
): Promise<ModalBlock[]> {
  const { songId, civicLevelSlug, modalKey, includeDrafts = false } = options;

  const civicLevel = await getCivicLevelBySlug(civicLevelSlug);
  if (!civicLevel) {
    console.error('[fetchModalBlocks] Civic level not found:', civicLevelSlug);
    return [];
  }

  let query = supabase
    .from('song_modal_blocks')
    .select('*')
    .eq('song_id', songId)
    .eq('civic_level_id', civicLevel.id)
    .eq('modal_key', modalKey)
    .eq('enabled', true)
    .order('sort_order');

  if (!includeDrafts) {
    query = query.eq('status', 'published');
  }

  const { data, error } = await query;

  if (error) {
    console.error('[fetchModalBlocks] Error:', error);
    return [];
  }

  return data || [];
}

// ============================================================================
// FETCH PATHWAY CARDS
// ============================================================================

export interface FetchCardsOptions {
  songId: string;
  civicLevelSlug: string;
  includeDrafts?: boolean; // Admin or Preview mode
}

export async function fetchPathwayCards(
  options: FetchCardsOptions
): Promise<PathwayCard[]> {
  const { songId, civicLevelSlug, includeDrafts = false } = options;

  const civicLevel = await getCivicLevelBySlug(civicLevelSlug);
  if (!civicLevel) {
    console.error('[fetchPathwayCards] Civic level not found:', civicLevelSlug);
    return [];
  }

  let query = supabase
    .from('song_pathway_cards')
    .select('*')
    .eq('song_id', songId)
    .eq('civic_level_id', civicLevel.id)
    .eq('enabled', true)
    .order('sort_order');

  if (!includeDrafts) {
    query = query.eq('status', 'published');
  }

  const { data, error } = await query;

  if (error) {
    console.error('[fetchPathwayCards] Error:', error);
    return [];
  }

  return data || [];
}

// ============================================================================
// SAVE MODAL BLOCKS
// ============================================================================

export async function saveModalBlock(block: Partial<ModalBlock>): Promise<{ success: boolean; error?: string }> {
  try {
    if (block.id) {
      // Update existing
      const { error } = await supabase
        .from('song_modal_blocks')
        .update({
          block_type: block.block_type,
          sort_order: block.sort_order,
          enabled: block.enabled,
          status: block.status,
          tone: block.tone,
          block_title: block.block_title,
          block_body: block.block_body,
          block_prompt: block.block_prompt,
          items: block.items,
          content: block.content,
          meta: block.meta,
        })
        .eq('id', block.id);

      if (error) throw error;
    } else {
      // Insert new
      const { error } = await supabase
        .from('song_modal_blocks')
        .insert([block]);

      if (error) throw error;
    }

    return { success: true };
  } catch (error: any) {
    console.error('[saveModalBlock] Error:', error);
    return { success: false, error: error.message };
  }
}

export async function deleteModalBlock(blockId: string): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase
      .from('song_modal_blocks')
      .delete()
      .eq('id', blockId);

    if (error) throw error;
    return { success: true };
  } catch (error: any) {
    console.error('[deleteModalBlock] Error:', error);
    return { success: false, error: error.message };
  }
}

// ============================================================================
// SAVE PATHWAY CARDS
// ============================================================================

export async function savePathwayCard(card: Partial<PathwayCard>): Promise<{ success: boolean; error?: string }> {
  try {
    if (card.id) {
      // Update existing
      const { error } = await supabase
        .from('song_pathway_cards')
        .update({
          sort_order: card.sort_order,
          enabled: card.enabled,
          status: card.status,
          title: card.title,
          description: card.description,
          action_type: card.action_type,
          region_scope: card.region_scope,
          cta_label: card.cta_label,
          url: card.url,
          source_tag: card.source_tag,
        })
        .eq('id', card.id);

      if (error) throw error;
    } else {
      // Insert new
      const { error } = await supabase
        .from('song_pathway_cards')
        .insert([card]);

      if (error) throw error;
    }

    return { success: true };
  } catch (error: any) {
    console.error('[savePathwayCard] Error:', error);
    return { success: false, error: error.message };
  }
}

export async function deletePathwayCard(cardId: string): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase
      .from('song_pathway_cards')
      .delete()
      .eq('id', cardId);

    if (error) throw error;
    return { success: true };
  } catch (error: any) {
    console.error('[deletePathwayCard] Error:', error);
    return { success: false, error: error.message };
  }
}

// ============================================================================
// CHECK IF CONTENT EXISTS
// ============================================================================

export async function hasActionModalContent(
  songId: string,
  civicLevelSlug: string
): Promise<boolean> {
  const civicLevel = await getCivicLevelBySlug(civicLevelSlug);
  if (!civicLevel) return false;

  const { data: blocks } = await supabase
    .from('song_modal_blocks')
    .select('id')
    .eq('song_id', songId)
    .eq('civic_level_id', civicLevel.id)
    .limit(1);

  const { data: cards } = await supabase
    .from('song_pathway_cards')
    .select('id')
    .eq('song_id', songId)
    .eq('civic_level_id', civicLevel.id)
    .limit(1);

  return (blocks && blocks.length > 0) || (cards && cards.length > 0);
}
